import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import { getLocalParticipant } from '../../../base/participants/functions';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import Tabs from '../../../base/ui/components/web/Tabs';
import { arePollsDisabled } from '../../../conference/functions.any';
import PollsPane from '../../../polls/components/web/PollsPane';
import { sendMessage, setChatDocked, setIsPollsTabFocused, toggleChat } from '../../actions.web';
import { CHAT_SIZE, CHAT_TABS, SMALL_WIDTH_THRESHOLD } from '../../constants';
import ChatHeader from './ChatHeader';
import ChatInput from './ChatInput';
import DisplayNameForm from './DisplayNameForm';
import KeyboardAvoider from './KeyboardAvoider';
import MessageContainer from './MessageContainer';
import MessageRecipient from './MessageRecipient';
const CHAT_FONT_SIZE_OPTIONS = [
    {
        id: 'regular',
        label: 'A',
        lineHeight: '20px',
        messageFontSize: '14px',
        metaFontSize: '12px'
    },
    {
        id: 'large',
        label: 'A+',
        lineHeight: '24px',
        messageFontSize: '16px',
        metaFontSize: '13px'
    },
    {
        id: 'extra-large',
        label: 'A++',
        lineHeight: '28px',
        messageFontSize: '18px',
        metaFontSize: '14px'
    }
];
const useStyles = makeStyles()(theme => {
    return {
        container: {
            backgroundColor: theme.palette.ui01,
            color: theme.palette.text01,
            flexShrink: 0,
            overflow: 'hidden',
            position: 'relative',
            transition: 'width .16s ease-in-out',
            width: `${CHAT_SIZE}px`,
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 300,
            '@media (max-width: 580px)': {
                height: '100dvh',
                position: 'fixed',
                left: 0,
                right: 0,
                top: 0,
                width: 'auto'
            },
            '*': {
                userSelect: 'text',
                '-webkit-user-select': 'text'
            }
        },
        chatHeader: {
            minHeight: 60,
            flexShrink: 0,
            position: 'relative',
            width: '100%',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
            alignItems: 'center',
            boxSizing: 'border-box',
            color: theme.palette.text01,
            ...withPixelLineHeight(theme.typography.heading6),
            '& button': { minHeight: 44, minWidth: 44 },
            '.jitsi-icon': {
                cursor: 'pointer'
            }
        },
        chatPanel: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
        },
        chatPanelNoTabs: {
            minHeight: 0
        },
        fontControls: {
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.ui03}`,
            display: 'flex',
            gap: theme.spacing(2),
            minHeight: 48,
            flexShrink: 0,
            justifyContent: 'flex-end',
            padding: `0 ${theme.spacing(4)}`,
            boxSizing: 'border-box'
        },
        fontControlLabel: {
            color: theme.palette.text02,
            fontSize: '12px',
            fontWeight: 600
        },
        fontControlButton: {
            alignItems: 'center',
            background: theme.palette.ui02,
            border: `1px solid ${theme.palette.ui04}`,
            borderRadius: '4px',
            color: theme.palette.text01,
            cursor: 'pointer',
            display: 'inline-flex',
            fontSize: '14px',
            fontWeight: 700,
            height: '44px',
            justifyContent: 'center',
            minWidth: '44px',
            padding: '0 8px',
            '&:disabled': {
                cursor: 'default',
                opacity: 0.45
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.action01}`,
                outlineOffset: '2px'
            }
        },
        interpreterTools: {
            borderBottom: `1px solid ${theme.palette.ui03}`, padding: theme.spacing(2),
            maxHeight: '40%', overflowY: 'auto', flexShrink: 0,
            '& summary': { cursor: 'pointer', minHeight: 44, display: 'flex', alignItems: 'center',
                color: theme.palette.text01, ...withPixelLineHeight(theme.typography.bodyShortBold) },
            '& summary:focus-visible': { outline: `2px solid ${theme.palette.focus01}` }
        },
        pollsPanel: {
            flex: 1, minHeight: 0, overflowY: 'auto'
        }
    };
});
const Chat = ({ _isModal, _isOpen, _isPollsEnabled, _isPollsTabFocused, _messages, _nbUnreadMessages, _nbUnreadPolls, _onSendMessage, _onToggleChat, _onToggleChatTab, _onTogglePollsTab, _showNamePrompt, dispatch, persistent = false, interpreterTools, t }) => {
    const { classes, cx } = useStyles();
    const [chatFontSizeIndex, setChatFontSizeIndex] = useState(1);
    const [dockSide, setDockSide] = useState('right');
    useEffect(() => {
        dispatch(setChatDocked(persistent));
        return () => { dispatch(setChatDocked(false)); };
    }, [persistent, dispatch]);
    const chatFontSize = CHAT_FONT_SIZE_OPTIONS[chatFontSizeIndex];
    const chatFontStyle = {
        '--malka-chat-message-font-size': chatFontSize.messageFontSize,
        '--malka-chat-meta-font-size': chatFontSize.metaFontSize,
        '--malka-chat-line-height': chatFontSize.lineHeight,
        order: interpreterTools && dockSide === 'right' ? 1 : -1
    };
    /**
    * Sends a text message.
    *
    * @private
    * @param {string} text - The text message to be sent.
    * @returns {void}
    * @type {Function}
    */
    const onSendMessage = useCallback((text) => {
        dispatch(sendMessage(text));
    }, []);
    /**
    * Toggles the chat window.
    *
    * @returns {Function}
    */
    const onToggleChat = useCallback(() => {
        if (!persistent)
            dispatch(toggleChat());
    }, [persistent]);
    /**
     * Click handler for the chat sidenav.
     *
     * @param {KeyboardEvent} event - Esc key click to close the popup.
     * @returns {void}
     */
    const onEscClick = useCallback((event) => {
        if (event.key === 'Escape' && _isOpen) {
            event.preventDefault();
            event.stopPropagation();
            onToggleChat();
        }
    }, [_isOpen, onToggleChat]);
    /**
     * Change selected tab.
     *
     * @param {string} id - Id of the clicked tab.
     * @returns {void}
     */
    const onChangeTab = useCallback((id) => {
        dispatch(setIsPollsTabFocused(id !== CHAT_TABS.CHAT));
    }, []);
    /**
     * Returns a React Element for showing chat messages and a form to send new
     * chat messages.
     *
     * @private
     * @returns {ReactElement}
     */
    function renderChat() {
        return (React.createElement(React.Fragment, null,
            _isPollsEnabled && renderTabs(),
            React.createElement("div", { "aria-label": !_isPollsEnabled ? 'Chat messages' : undefined, "aria-labelledby": _isPollsEnabled ? CHAT_TABS.CHAT : undefined, className: cx(classes.chatPanel, !_isPollsEnabled && classes.chatPanelNoTabs, _isPollsTabFocused && 'hide'), id: `${CHAT_TABS.CHAT}-panel`, role: _isPollsEnabled ? 'tabpanel' : 'region', tabIndex: 0 },
                React.createElement("div", { "aria-label": 'Chat text size', className: cx('chat-font-size-controls', classes.fontControls), role: 'group' },
                    React.createElement("button", { "aria-label": 'Decrease chat font size', className: classes.fontControlButton, disabled: chatFontSizeIndex === 0, onClick: () => setChatFontSizeIndex(index => Math.max(0, index - 1)), type: 'button' }, "A-"),
                    React.createElement("span", { "aria-live": 'polite', className: classes.fontControlLabel }, chatFontSize.label),
                    React.createElement("button", { "aria-label": 'Increase chat font size', className: classes.fontControlButton, disabled: chatFontSizeIndex === CHAT_FONT_SIZE_OPTIONS.length - 1, onClick: () => setChatFontSizeIndex(index => Math.min(CHAT_FONT_SIZE_OPTIONS.length - 1, index + 1)), type: 'button' }, "A+")),
                React.createElement(MessageContainer, { messages: _messages }),
                React.createElement(MessageRecipient, null),
                React.createElement(ChatInput, { autoFocus: !persistent, onSend: onSendMessage })),
            _isPollsEnabled && (React.createElement(React.Fragment, null,
                React.createElement("div", { "aria-labelledby": CHAT_TABS.POLLS, className: cx(classes.pollsPanel, !_isPollsTabFocused && 'hide'), id: `${CHAT_TABS.POLLS}-panel`, role: 'tabpanel', tabIndex: 0 },
                    React.createElement(PollsPane, null)),
                React.createElement(KeyboardAvoider, null)))));
    }
    /**
     * Returns a React Element showing the Chat and Polls tab.
     *
     * @private
     * @returns {ReactElement}
     */
    function renderTabs() {
        return (React.createElement(Tabs, { accessibilityLabel: t(_isPollsEnabled ? 'chat.titleWithPolls' : 'chat.title'), onChange: onChangeTab, selected: _isPollsTabFocused ? CHAT_TABS.POLLS : CHAT_TABS.CHAT, tabs: [{
                    accessibilityLabel: t('chat.tabs.chat'),
                    countBadge: _isPollsTabFocused && _nbUnreadMessages > 0 ? _nbUnreadMessages : undefined,
                    id: CHAT_TABS.CHAT,
                    controlsId: `${CHAT_TABS.CHAT}-panel`,
                    label: t('chat.tabs.chat')
                }, {
                    accessibilityLabel: t('chat.tabs.polls'),
                    countBadge: !_isPollsTabFocused && _nbUnreadPolls > 0 ? _nbUnreadPolls : undefined,
                    id: CHAT_TABS.POLLS,
                    controlsId: `${CHAT_TABS.POLLS}-panel`,
                    label: t('chat.tabs.polls')
                }
            ] }));
    }
    return ((_isOpen || persistent) ? React.createElement("div", { className: classes.container, "aria-label": 'Call chat', role: 'complementary', "data-dock": interpreterTools ? dockSide : 'left', id: 'sideToolbarContainer', onKeyDown: onEscClick, style: chatFontStyle },
        React.createElement(ChatHeader, { className: cx('chat-header', classes.chatHeader), dockSide: interpreterTools ? dockSide : undefined, onDockChange: () => setDockSide(side => side === 'right' ? 'left' : 'right'), persistent: persistent, isPollsEnabled: _isPollsEnabled, onCancel: onToggleChat }),
        interpreterTools && React.createElement("div", { className: classes.interpreterTools }, interpreterTools),
        _showNamePrompt
            ? React.createElement(DisplayNameForm, { isPollsEnabled: _isPollsEnabled })
            : renderChat()) : null);
};
/**
 * Maps (parts of) the redux state to {@link Chat} React {@code Component}
 * props.
 *
 * @param {Object} state - The redux store/state.
 * @param {any} _ownProps - Components' own props.
 * @private
 * @returns {{
 *     _isModal: boolean,
 *     _isOpen: boolean,
 *     _isPollsEnabled: boolean,
 *     _isPollsTabFocused: boolean,
 *     _messages: Array<Object>,
 *     _nbUnreadMessages: number,
 *     _nbUnreadPolls: number,
 *     _showNamePrompt: boolean
 * }}
 */
function _mapStateToProps(state, _ownProps) {
    const { isOpen, isPollsTabFocused, messages, nbUnreadMessages } = state['features/chat'];
    const { nbUnreadPolls } = state['features/polls'];
    const _localParticipant = getLocalParticipant(state);
    return {
        _isModal: window.innerWidth <= SMALL_WIDTH_THRESHOLD,
        _isOpen: isOpen,
        _isPollsEnabled: !arePollsDisabled(state),
        _isPollsTabFocused: isPollsTabFocused,
        _messages: messages,
        _nbUnreadMessages: nbUnreadMessages,
        _nbUnreadPolls: nbUnreadPolls,
        _showNamePrompt: !_localParticipant?.name
    };
}
export default translate(connect(_mapStateToProps)(Chat));
